import FileUploader from 'components/UploaderAndDownloader/FileUploader';
import React, { useCallback, useContext } from 'react';
import { createAppError } from 'utils/helpers';
import { AppContext, IAppContext } from 'appState/appContext';
import { IFileData } from 'types';
import { TTypeOfModalWindow } from '../../../../../../createVideoState/createVideoContext';
import { useAdditionalValidation } from 'components/WithAdditionalValidation/useAdditionalValidation';
import { postMediaFile } from 'backend/additionalInformation';
import style from './VideoFileBlock.module.scss';

interface IVideoFileBlock {
	changeFileData: (value: IFileData) => void;
	changeTypeOfModalWindow: (value: TTypeOfModalWindow) => void;
	fileData: IFileData;
}

const VideoFileBlock: React.FC<IVideoFileBlock> = ({ fileData, changeFileData, changeTypeOfModalWindow }) => {
	const { showLoader, hideLoader, addAppError } = useContext<IAppContext>(AppContext);
	const { isShowValidationError, validationErrorMessage } = useAdditionalValidation(
		fileData.fileId,
		'shouldUploadVideoFile',
		'videoUpload',
	);

	const sendForm = useCallback(
		async (file: File, callback: (value: string) => void) => {
			const maxFileSizeMB = 150;
			if (file.size > 1000 * 1000 * maxFileSizeMB) {
				changeTypeOfModalWindow('fileSizeExceeded');
				return null;
			}
			showLoader();
			const formData = new FormData();
			formData.append('file', file, file.name);

			try {
				const { data } = await postMediaFile({ data: formData });
				changeFileData({ fileId: data.fileId, fileHash: data.fileHash, fileName: file.name });
				callback(file.name);
				hideLoader();
			} catch (err) {
				hideLoader();
				const error = err as Error;
				const appError = createAppError(error);
				addAppError(appError);
			}
		},
		[changeTypeOfModalWindow, changeFileData],
	);

	const resetFile = useCallback(() => {
		changeFileData({ fileId: '', fileHash: null, fileName: null });
	}, [changeFileData]);

	return (
		<div className={style.container} data-test="VideoFileBlock">
			<FileUploader
				fileType="mp4"
				onFileLoad={sendForm}
				onFileReset={resetFile}
				id="videoUpload"
				label="uploadVideoFileButton"
				requiredValidationMessage={isShowValidationError ? validationErrorMessage : undefined}
				filePath={fileData.fileName}
			/>
		</div>
	);
};

export default React.memo(VideoFileBlock);
